import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  PageHeader, EmptyState, QuickActionBar, toActionContacts, SectionCard, Tabs, DataTable,
  DescriptionList, AssociationCard, Badge, StanceBadge, Avatar, Button,
  type DLRow, type BadgeTone, type TabDef, type Column,
} from '@/components/ui'
import { RecordHero } from '@/components/workspace'
import { Timeline } from '@/components/timeline'
import {
  UserRound, Network, Briefcase, Pencil, Newspaper, MessageSquare, Warehouse,
  FileText, StickyNote, Mail, Phone, CalendarDays, ListChecks,
  Linkedin, Instagram, Facebook, Youtube, Music2, Star, Flag, Heart,
} from 'lucide-react'
import { useStore, useHasPermission } from '@/store/useStore'
import {
  accountById, contactById, contactName, trackById, propertiesForAccount,
  actionItemsForRef, latest,
} from '@/lib/selectors'
import { date, money, pct } from '@/lib/format'
import { ReportIssueModal } from './ReportIssueModal'
import { SpouseModal } from './SpouseModal'
import type {
  Account, Activity, ActivityType, ContactType, InfluenceLevel, Opportunity, Property, Social,
} from '@/types/entities'
import { ContactForm } from './ContactForm'
import './ContactDetail.css'

const TYPE_TONE: Record<ContactType, BadgeTone> = {
  Investor: 'navy', '3PM': 'info', Broker: 'neutral', Municipal: 'warn',
  'WPI Employee': 'neutral', Vendor: 'neutral', Personal: 'neutral', JV: 'info',
}
const INFLUENCE_TONE: Record<InfluenceLevel, BadgeTone> = { High: 'info', Medium: 'neutral', Low: 'neutral' }

const SOCIALS: { key: keyof Social; label: string; icon: typeof Linkedin }[] = [
  { key: 'linkedIn', label: 'LinkedIn', icon: Linkedin },
  { key: 'instagram', label: 'Instagram', icon: Instagram },
  { key: 'facebook', label: 'Facebook', icon: Facebook },
  { key: 'tiktok', label: 'TikTok', icon: Music2 },
  { key: 'youtube', label: 'YouTube', icon: Youtube },
]

const tel = (raw?: string) => (raw ? `tel:${raw.replace(/[^+\d]/g, '')}` : undefined)
function addressLine(a?: Account['hqAddress']): string | undefined {
  if (!a) return undefined
  const parts = [a.line1, [a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}

/**
 * The route element, keyed by the contact id (2026-09-29). React Router keeps
 * ONE ContactDetail alive while only `:id` changes, so jumping from contact A
 * to contact B (global search goes contact → contact without leaving the
 * page) used to carry A's page state — the open tab, an open editor, the
 * report and spouse dialogs — over to B. The editor was the dangerous one: it
 * held A's draft, rendered it under B's name, and Save wrote A onto B. A new
 * id is now a new page. (ContactForm also re-reads its contact on every open;
 * see the note there.)
 */
export default function ContactDetailPage() {
  const { id } = useParams()
  return <ContactDetail key={id ?? ''} />
}

function ContactDetail() {
  const { id } = useParams()
  const data = useStore((s) => s.data)
  const patchStore = useStore((s) => s.patch)
  const canManage = useHasPermission('crm.manage')
  const contact = contactById(data, id)
  const [tab, setTab] = useState('overview')
  const [editing, setEditing] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [spouseOpen, setSpouseOpen] = useState(false)

  const account = contact ? (accountById(data, contact.accountId) as Account | undefined) : undefined

  const opps = useMemo<Opportunity[]>(() => {
    if (!contact) return []
    return data.opportunities.filter((o) =>
      o.primaryContactId === contact.id || o.sourceContactId === contact.id
      || o.contactRoles?.some((r) => r.contactId === contact.id)
      || o.stakeholderIds?.includes(contact.id))
  }, [data.opportunities, contact])

  const activities = useMemo<Activity[]>(() => {
    if (!contact) return []
    return data.activities.filter((a) =>
      a.withContacts?.includes(contact.id)
      || a.attachedTo.some((r) => r.kind === 'contact' && r.id === contact.id)
      || a.attachedTo.some((r) => r.kind === 'account' && r.id === contact.accountId))
  }, [data.activities, contact])

  const portfolio = useMemo<Property[]>(
    () => (contact?.accountId ? propertiesForAccount(data, contact.accountId) : []),
    [data, contact],
  )
  const tasks = useMemo(() => (contact ? actionItemsForRef(data, 'contact', contact.id) : []), [data, contact])

  if (!contact) {
    return (
      <>
        <PageHeader eyebrow="Contact" title="Contact not found" />
        <div className="card">
          <EmptyState icon={<UserRound size={40} />} title="No such contact"
            hint={<>We couldn&rsquo;t find a contact with id <code>{id}</code>.</>}
            action={<Link to="/contacts"><Button variant="navy">Back to Contacts</Button></Link>} />
        </div>
      </>
    )
  }

  const name = contactName(contact)
  const byType = (t: ActivityType) => activities.filter((a) => a.type === t)

  const isPrimary = account?.primaryContactId === contact.id
    || data.tracks.some((t) => t.primaryContactId === contact.id)

  const badges = (
    <>
      {contact.archived && <Badge tone="alert" variant="solid">Archived</Badge>}
      {isPrimary && <Badge tone="info" variant="solid"><Star size={11} /> Primary Contact</Badge>}
      {contact.isWpi && <Badge tone="navy" variant="solid">WPI Employee</Badge>}
      {contact.type && !contact.isWpi && <Badge tone={TYPE_TONE[contact.type] ?? 'neutral'} variant="soft">{contact.type}</Badge>}
      {contact.stance && <StanceBadge stance={contact.stance} />}
      {contact.influence && <Badge tone={INFLUENCE_TONE[contact.influence]} variant="outline">{contact.influence} Influence</Badge>}
    </>
  )

  /* ---- left-rail panels ---- */
  const addr = contact.officeAddress ?? (contact.sameAsHQ ? account?.hqAddress : undefined)
  const keyInfo: DLRow[] = ([
    { label: 'Title', value: contact.title ?? '—' },
    { label: 'Department', value: contact.department ?? '—' },
    { label: 'Account', value: account ? <Link to={`/accounts/${account.id}`} className="cd-link">{account.name}</Link> : '—' },
    { label: 'Status', value: contact.status ?? '—' },
    { label: 'Work Email', value: contact.workEmail ? <a href={`mailto:${contact.workEmail}`} className="cd-link">{contact.workEmail}</a> : '—' },
    { label: 'Personal Email', value: contact.personalEmail ? <a href={`mailto:${contact.personalEmail}`} className="cd-link">{contact.personalEmail}</a> : '—' },
    { label: 'Cell Phone', value: contact.cellPhone ? <a href={tel(contact.cellPhone)} className="cd-link">{contact.cellPhone}</a> : '—' },
    { label: 'Office Phone', value: contact.officePhone ? <a href={tel(contact.officePhone)} className="cd-link">{contact.officePhone}</a> : '—' },
    { label: 'Office', value: addressLine(addr) ?? (contact.sameAsHQ ? 'Same as HQ' : '—') },
    { label: 'Country', value: contact.country ?? '—' },
  ] as DLRow[]).filter((r) => r.value !== '—')

  const familyBits = [
    contact.married ? 'Married' : undefined,
    contact.spouse ? `Spouse: ${contact.spouse}` : undefined,
    contact.children?.length ? `Children: ${contact.children.join(', ')}` : undefined,
    contact.g2 ? `G2: ${contact.g2}` : undefined,
  ].filter(Boolean).join(' · ')
  const fmtYears = (ys?: number[]) => (ys ?? []).slice().sort().map((y) => `’${String(y).slice(2)}`).join(' ')
  const attendedYears = fmtYears(contact.symposiumYears)
  const invitedYears = fmtYears(contact.symposiumInvitedYears)
  const shirtBits = [
    contact.shirtSize,
    contact.shirtStyle === 'W' ? 'Women’s cut' : contact.shirtStyle === 'M' ? 'Men’s cut' : undefined,
  ].filter(Boolean).join(' · ')
  const personal: DLRow[] = ([
    { label: 'Birthday', value: contact.birthday ? date(contact.birthday, 'long') : '—' },
    { label: 'Holiday', value: contact.holiday ?? '—' },
    { label: 'Education', value: [contact.degree, contact.almaMater].filter(Boolean).join(', ') || '—' },
    { label: 'Hobby / Interest', value: contact.hobby ?? '—' },
    { label: 'Family', value: familyBits || '—' },
    { label: 'Shirt Size', value: shirtBits || '—' },
    { label: 'Source / Referred By', value: contact.sourceReferredBy ?? '—' },
    {
      label: 'WPI Investor Symposium',
      value: contact.investorSymposium == null && !attendedYears && !invitedYears ? '—' : (
        <span className="cd-sym">
          {contact.investorSymposium != null && (
            <Badge tone={contact.investorSymposium ? 'ok' : 'neutral'} variant="soft">{contact.investorSymposium ? 'Yes' : 'No'}</Badge>
          )}
          {attendedYears && <span> Attended {attendedYears}</span>}
          {invitedYears && <span className="muted">{attendedYears ? ' · ' : ' '}Invited {invitedYears}</span>}
        </span>
      ),
    },
  ] as DLRow[]).filter((r) => r.value !== '—')

  const socialLinks = SOCIALS.filter((s) => contact.social?.[s.key])

  /* ---- tabs ---- */
  const tabs: TabDef[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'news', label: 'News & Press' },
    { id: 'social', label: 'Social Posts' },
    { id: 'opportunities', label: 'Opportunities', count: opps.length || undefined },
    { id: 'portfolio', label: 'Portfolio', count: portfolio.length || undefined },
    { id: 'files', label: 'Files' },
    { id: 'notes', label: 'Notes', count: byType('Note').length || undefined },
    { id: 'emails', label: 'Emails', count: byType('Email').length || undefined },
    { id: 'calls', label: 'Calls', count: byType('Call').length || undefined },
    { id: 'tasks', label: 'Tasks', count: tasks.length || undefined },
    { id: 'meetings', label: 'Meetings', count: byType('Meeting').length || undefined },
  ]

  const portfolioCols: Column<Property>[] = [
    { key: 'name', header: 'Property', sortValue: (p) => p.name,
      render: (p) => <Link to={`/properties/${p.id}`} className="cd-link" style={{ fontWeight: 600 }}>{p.name}</Link> },
    { key: 'lcode', header: 'Lcode', render: (p) => <span className="muted">{p.externalIds?.lcode ?? '—'}</span> },
    { key: 'occ', header: 'Occupancy', align: 'right', sortValue: (p) => latest(p.occupancyPct) ?? -1,
      render: (p) => <span className="num">{pct(latest(p.occupancyPct))}</span> },
    { key: 'state', header: 'Status', align: 'center', render: (p) => <Badge tone="neutral" variant="soft">{p.state}</Badge> },
  ]

  return (
    <div className="contact-detail">
      <RecordHero
        tone="light"
        breadcrumb={<span><Link to="/contacts">Contacts</Link> <span className="dim">/</span> {name}</span>}
        eyebrow={<span className="cd-hero-id">
          <Avatar name={name} size={26} tone={contact.isWpi ? 'slate' : 'navy'} />
          {account ? <Link to={`/accounts/${account.id}`}>{account.name}</Link> : 'Contact'}
        </span>}
        title={name}
        badges={badges}
        actions={
          <>
            {contact.archived && canManage && (
              <Button variant="default" size="sm" onClick={() => patchStore('contacts', contact.id, { archived: false })}>
                Unarchive
              </Button>
            )}
            {/* crm.use, on purpose — see ReportIssueModal. */}
            <Button variant="default" size="sm" icon={<Flag size={14} />} onClick={() => setReporting(true)}>
              Report a problem
            </Button>
            {/* Naming a spouse is an ordinary field edit; only the optional
                "combine their record" step inside needs crm.manage. */}
            <Button variant="default" size="sm" icon={<Heart size={14} />} onClick={() => setSpouseOpen(true)}>
              {contact.spouse ? 'Edit spouse' : 'Add spouse'}
            </Button>
            <Button variant="navy" size="sm" icon={<Pencil size={14} />} onClick={() => setEditing(true)}>Edit</Button>
          </>
        }
      />
      <ReportIssueModal
        open={reporting}
        onClose={() => setReporting(false)}
        contactId={contact.id}
        contactName={name}
      />
      {spouseOpen && (
        <SpouseModal open={spouseOpen} onClose={() => setSpouseOpen(false)} contact={contact} />
      )}

      <QuickActionBar
        contacts={toActionContacts([contact])}
        attachTo={{ kind: 'contact', id: contact.id, label: name }}
        contextLabel={name}
      />

      <div className="cd-layout">
        {/* LEFT — profile */}
        <div className="cd-col">
          <SectionCard eyebrow="Profile" title="Key Information">
            {keyInfo.length ? <DescriptionList rows={keyInfo} /> : <EmptyState title="No contact details recorded" />}
          </SectionCard>
          <SectionCard eyebrow="Relationship" title="Personal Details">
            {personal.length ? <DescriptionList rows={personal} /> : <EmptyState title="No personal details recorded" />}
          </SectionCard>
          <SectionCard eyebrow="Online" title="Socials">
            {socialLinks.length ? (
              <div className="cd-socials">
                {socialLinks.map((s) => {
                  const Icon = s.icon
                  const href = contact.social![s.key]!
                  return (
                    <a key={s.key} className="cd-social" href={href.startsWith('http') ? href : `https://${href}`} target="_blank" rel="noreferrer">
                      <Icon size={15} /><span>{s.label}</span>
                    </a>
                  )
                })}
              </div>
            ) : <EmptyState title="No social profiles linked" />}
          </SectionCard>
        </div>

        {/* RIGHT — tabbed */}
        <div className="cd-col">
          <div className="cd-tabbar"><Tabs tabs={tabs} active={tab} onChange={setTab} /></div>

          {tab === 'overview' && (
            <>
              {contact.description && <SectionCard eyebrow="Summary" title="About"><p className="cd-bio">{contact.description}</p></SectionCard>}
              <AssociationCard title="Track Memberships" count={(contact.trackMemberships ?? []).length} icon={<Network size={14} />}>
                {(contact.trackMemberships ?? []).length === 0 ? (
                  <EmptyState title="No track memberships" hint="Not yet mapped to a relationship track." />
                ) : (contact.trackMemberships ?? []).map((m) => {
                  const track = trackById(data, m.trackId)
                  const trackAcct = track ? accountById(data, track.accountId) : undefined
                  return (
                    <div className="cd-track" key={m.trackId}>
                      <div className="cd-track-top">
                        {track && trackAcct
                          ? <Link to={`/accounts/${trackAcct.id}/contacts`} className="cd-track-name truncate">{track.name}</Link>
                          : <span className="cd-track-name truncate">{track?.name ?? m.trackId}</span>}
                        {m.stance && <StanceBadge stance={m.stance} />}
                      </div>
                      <div className="cd-track-role">{m.role}</div>
                    </div>
                  )
                })}
              </AssociationCard>
            </>
          )}

          {tab === 'news' && <SectionCard eyebrow="External" title={<>News &amp; <b>Press</b></>}>
            <EmptyState icon={<Newspaper size={32} />} title="No press tracked yet"
              hint="The PRISM external-intelligence feed will surface news and filings about this contact and their firm here." />
          </SectionCard>}

          {tab === 'social' && <SectionCard eyebrow="External" title={<>Social <b>Posts</b></>}>
            <EmptyState icon={<MessageSquare size={32} />} title="No social posts captured"
              hint="Recent public posts from this contact's linked profiles will appear here when the social feed is connected." />
          </SectionCard>}

          {tab === 'opportunities' && <SectionCard eyebrow="Pipeline" title="Opportunities">
            {opps.length === 0 ? <EmptyState icon={<Briefcase size={32} />} title="No related opportunities" />
              : <div className="cd-opps">{opps.map((o) => {
                  const role = o.primaryContactId === contact.id ? (o.primaryContactRole ?? 'Primary Contact')
                    : o.sourceContactId === contact.id ? 'Source'
                    : (o.contactRoles?.find((r) => r.contactId === contact.id)?.role ?? 'Stakeholder')
                  return (
                    <div className="cd-opp" key={o.id}>
                      <div className="cd-opp-main">
                        <Link to={`/opportunities/${o.id}`} className="cd-opp-name truncate">{o.name}</Link>
                        <span className="cd-opp-sub">{o.type} · {role}</span>
                      </div>
                      <div className="cd-opp-meta">
                        {o.dealSize != null && <span className="num muted">{money(o.dealSize, { compact: true })}</span>}
                        <Badge tone="neutral" variant="soft">{o.salesStage}</Badge>
                      </div>
                    </div>
                  )
                })}</div>}
          </SectionCard>}

          {tab === 'portfolio' && <SectionCard eyebrow="Exposure" title="Portfolio"
            right={portfolio.length ? <span className="muted">{portfolio.length} properties</span> : undefined}>
            {portfolio.length === 0 ? <EmptyState icon={<Warehouse size={32} />} title="No associated portfolio" hint="Properties under this contact's account appear here." />
              : <DataTable<Property> columns={portfolioCols} rows={portfolio} getRowKey={(p) => p.id}
                  initialSort={{ key: 'occ', dir: 'desc' }} dense />}
          </SectionCard>}

          {tab === 'files' && <SectionCard eyebrow="Documents" title="Files">
            <EmptyState icon={<FileText size={32} />} title="No files attached"
              hint="Documents from SharePoint / OneDrive associated with this contact will appear here." />
          </SectionCard>}

          {tab === 'notes' && <ActivityTab title="Notes" icon={<StickyNote size={32} />} items={byType('Note')} empty="No notes logged" />}
          {tab === 'emails' && <ActivityTab title="Emails" icon={<Mail size={32} />} items={byType('Email')} empty="No emails logged" />}
          {tab === 'calls' && <ActivityTab title="Calls" icon={<Phone size={32} />} items={byType('Call')} empty="No calls logged" />}
          {tab === 'meetings' && <ActivityTab title="Meetings" icon={<CalendarDays size={32} />} items={byType('Meeting')} empty="No meetings logged" />}

          {tab === 'tasks' && <SectionCard eyebrow="Execution" title="Tasks">
            {tasks.length === 0 ? <EmptyState icon={<ListChecks size={32} />} title="No tasks for this contact" />
              : <div className="cd-tasks">{tasks.map((t) => (
                  <div className="cd-task" key={t.id}>
                    <span className="cd-task-title">{t.title}</span>
                    <span className="cd-task-meta">
                      <Badge tone={t.priority === 'Critical' ? 'alert' : t.priority === 'High' ? 'warn' : 'neutral'} variant="soft">{t.priority}</Badge>
                      {t.dueDate && <span className="muted">{date(t.dueDate, 'med')}</span>}
                    </span>
                  </div>
                ))}</div>}
          </SectionCard>}
        </div>
      </div>

      <ContactForm open={editing} onClose={() => setEditing(false)} contact={contact} />
    </div>
  )
}

function ActivityTab({ title, icon, items, empty }: { title: string; icon: React.ReactNode; items: Activity[]; empty: string }) {
  return (
    <SectionCard eyebrow="History" title={title} right={items.length ? <span className="muted">{items.length} logged</span> : undefined}>
      {items.length === 0 ? <EmptyState icon={icon} title={empty} /> : <Timeline activities={items} />}
    </SectionCard>
  )
}
